<template lang="pug">
.chat-message-wrapper(:class='{"is-latest": isLatest, "is-me": false}')
  .chat-message-container(v-if='!editing', :class='{"is-latest": isLatest}')
    .chat-icon
      img.thread-comment__user-icon.a-user-icon(
        :src='mentorComment.user.avatar_url',
        :title='mentorComment.user.icon_title')
    header.chat-header
      h2.chat-header_name
        | {{ mentorComment.user.name }}
      time.chat-header_time(
        :class='{ "is-active": activating }',
        :datetime='mentorCommentableCreatedAt')
        | {{ createdAt }}
      time.chat-header_time(v-if="mentorComment.created_at != mentorComment.updated_at")
        | （編集済み {{ updatedAt }}）
      .chat-header_badge(v-if="shouldShowBadge")
        .a-badge.is-sm.is-secondary(
          :class='{"is-info": isActiveFilterBadge}',
          @click='toggleFilter',
          :data-filter-value='commentFilterValue',
        )
          .a-badge__inner
            i.fa-solid.fa-user(v-if="mentorComment.mentor_commentable_type === 'User'")
            i.fa-solid.fa-hand(v-if="mentorComment.mentor_commentable_type === 'Product'")
            i.fa-solid.fa-circle-question(v-if="mentorComment.mentor_commentable_type === 'Question'")
            span
              | {{mentorComment.mentor_commentable.name}}
      a.chat-header_link(
        v-if="shouldShowLink"
        :href='mentorComment.mentor_commentable.url'
        :target='"_blank"'
      )
        i.fa-solid.fa-arrow-up-right-from-square
    .chat-body
      .chat-message__description
        .a-chat-text.is-md(v-html='markdownDescription').js-contents-image
      .chat-body_actions.chat-body_actions__reactions
        reaction(
          v-bind:reactionable='mentorComment',
          v-bind:currentUser='currentUser',
          v-bind:reactionableId='reactionableId'
          v-bind:broadcastName='broadcastName',
        )
      .chat-body_actions(v-if="mentorComment.user.id == currentUser.id")
        ul.chat-body_actions_buttons
          // 編集アイコン
          li.chat-options__item
            .mentor-comment-actions__muted-action.is-de(
              @click='editMentorComment($event)',
              :class="{ 'is-processing': isUpdating || isDeleting }"
            )
              i.fa-solid.fa-pen(
                :class='{ "is-latest": isLatest }'
              )
          // 削除アイコン
          li.chat-options__item
            .mentor-comment-actions__muted-action.is-de(
              @click='deleteMentorComment',
              :class="{ 'is-processing': isDeleting }"
            )
              i.fa-solid.fa-trash
      // リアクションスタンプ
      .chat-reaction
        .thread-comment__reactions(:class='{ "thread-comment__reactions__hasReaction": hasAnyReaction }')
          reactions(
            v-bind:reactionable='mentorComment',
            v-bind:currentUser='currentUser',
            v-bind:reactionableId='reactionableId'
            v-bind:broadcastName='broadcastName',
          )
  // メッセージ編集用のフォーム
  .chat-message-edit-container(v-show='editing')
    .a-form-tabs.js-tabs
      .a-form-tabs__tab.js-tabs__tab(
        :class='{ "is-active": isActive("mentor-comment") }',
        @click='changeActiveTab("mentor-comment")')
        | コメント
      .a-form-tabs__tab.js-tabs__tab(
        :class='{ "is-active": isActive("preview") }',
        @click='changeActiveTab("preview")')
        | プレビュー
    .a-markdown-input.js-markdown-parent
      .a-markdown-input__inner.js-tabs__content(
        :class='{ "is-active": isActive("mentor-comment") }')
        .form-textarea
          .form-textarea__body
            textarea.a-text-input.a-markdown-input__textarea(
              v-model='body',
              name='new_mentor_comment[body]'
              :id='`js-mentor-comment-${this.mentorComment.id}`',
              :data-preview='`#js-comment-preview-${this.mentorComment.id}`',
              :data-input='`#js-comment-file-input-${this.mentorComment.id}`',
              @compositionstart="isComposing = true",
              @compositionend="isComposing = false",
              @keydown.stop='handleKeyDown'
            )
          .chat-form-footer
            .mentor-comment-actions
              ul.mentor-comment-actions__items
                // ファイル添付
                li.card-main-actions__item.is-sub
                  label.mentor-comment-actions__muted-action.is-de
                    i.fa-solid.fa-paperclip
                    input.new-mentor-comment-file-input(:id='`js-comment-file-input-${this.mentorComment.id}`', type='file', multiple hidden)
      .a-markdown-input__inner.js-tabs__content(
        :class='{ "is-active": isActive("preview") }')
        .a-chat-text.is-md.a-markdown-input__preview(
            :id='`js-comment-preview-${this.mentorComment.id}`')
    hr.a-border-tint
    .card-footer
      .card-main-actions
        .card-main-actions__items
          .card-main-actions__item
            button.a-button.is-sm.is-primary.is-block(@click='updateMentorComment',v-bind:disabled='!validation || isUpdating')
              | 更新
          .card-main-actions__item
            button.a-button.is-sm.is-secondary.is-block(@click='cancel')
              | キャンセル
</template>
<script>
import CSRF from 'csrf'
import ChatReaction from 'chat-reaction.vue'
import ChatReactions from 'chat-reactions.vue'
import MarkdownInitializer from 'markdown-initializer'
import TextareaInitializer from 'textarea-initializer'
import autosize from 'autosize'
import dayjs from 'dayjs'
import ja from 'dayjs/locale/ja'
import role from 'role'
import toast from 'toast'

dayjs.locale(ja)

export default {
  components: {
    reaction: ChatReaction,
    reactions: ChatReactions,
  },
  mixins: [role, toast],
  props: {
    mentorComment: { type: Object, required: true },
    currentUser: { type: Object, required: true },
    isLatest: { type: Boolean, required: true },
    // 未指定だとリアクションの描画をchat-reaction(s).vue側がローカルで行う
    // ポーリング方式へ統一したため、常に未指定
    broadcastName: { type: String, required: false, default: '' },
    filterOptions: { type: String, required: false },
    selectedFilterValue: { type: String, default: '' },
  },
  data() {
    return {
      body: '',
      editing: false,
      isCopied: false,
      tab: 'mentor-comment',
      activating: false,
      isComposing: false, // 日本語入力中かどうかのフラグ
      isUpdating: false, // 更新処理中かどうかのフラグを追加
      isDeleting: false, // 削除処理中かどうかのフラグ
    }
  },
  computed: {
    mentorCommentableCreatedAt() {
      return dayjs(this.mentorComment.mentor_commentable.created_at).format()
    },
    markdownDescription() {
      const markdownInitializer = new MarkdownInitializer()
      const messageContent = `${this.mentorComment.body}`
      return markdownInitializer.render(messageContent)
    },
    createdAt() {
      return dayjs(this.mentorComment.created_at).format('YYYY年MM月DD日(dd) HH:mm')
    },
    updatedAt() {
      return dayjs(this.mentorComment.updated_at).format('YYYY年MM月DD日(dd) HH:mm')
    },
    validation() {
      return this.body.length > 0
    },
    reactionableId() {
      return `MentorComment_${this.mentorComment.id}`
    },
    commentFilterValue() {
      const type = this.mentorComment.mentor_commentable_type;
      const id = this.mentorComment.mentor_commentable_id;
      return `${type},${id}`; // 例: "Company,123"
    },
    // このバッジが、現在アクティブなフィルターを表しているかどうかを判定する算出プロパティ
    isActiveFilterBadge() {
      // 親から受け取った selectedFilterValue が、このバッジ自身のフィルター値と一致するかどうか
      return this.selectedFilterValue === this.commentFilterValue;
    },
    shouldShowBadge() {
      const hasName = this.mentorComment.mentor_commentable.name;
      const allowedTypesForBadge = ['User', 'Product', 'Question'];
      const isAllowedType = allowedTypesForBadge.includes(this.mentorComment.mentor_commentable_type);

      return this.filterOptions && hasName && isAllowedType;
    },
    shouldShowLink() {
      const hasUrl = this.mentorComment.mentor_commentable.url;

      return this.filterOptions && hasUrl;
    },
    // リアクションが1件でも存在するかどうか
    hasAnyReaction() {
      return this.mentorComment.reaction_count.some(el => el.count !== 0);
    },
  },
  mounted() {
    TextareaInitializer.initialize(`#js-mentor-comment-${this.mentorComment.id}`)
    const mentorCommentAnchor = location.hash
    if (mentorCommentAnchor) {
      this.$nextTick(() => {
        location.replace(location.href)
      })
    }
  },
  methods: {
    isActive(tab) {
      return this.tab === tab
    },
    changeActiveTab(tab) {
      this.tab = tab
    },
    cancel() {
      this.body = this.mentorComment.body
      this.editing = false
      this.$nextTick(() => {
        Prism.highlightAll();
      })
    },
    editMentorComment(event) {
      // 処理中に編集モードに入らないようにガードする
      if (this.isUpdating || this.isDeleting) return;

      this.body = this.mentorComment.body;
      this.editing = true

      this.$nextTick(function () {
        TextareaInitializer.initialize(`#js-mentor-comment-${this.mentorComment.id}`)

        const textarea = document.querySelector(
          `#js-mentor-comment-${this.mentorComment.id}`
        )
        autosize.update(textarea)
        $(`.mentor-comment-id-${this.mentorComment.id}`).trigger('input')

        if (this.isLatest) {
          const latestChatMessage = document.querySelector('.chat-messages')
          latestChatMessage.scrollTop = latestChatMessage.scrollHeight;
        }
      })
    },
    async updateMentorComment() {
      // バリデーションは先頭に
      if (this.body.length < 1) {
        return;
      }

      // 処理中フラグを立て、UIを更新（ボタンを無効化）
      this.isUpdating = true;

      try {
        // リクエストに含めるパラメータを定義
        const params = {
          mentor_comment: { body: this.body },
          // WebSocketを再開する場合に戻す（doc/architecture.md §5.6）
          // broadcast: this.broadcastName,
        };

        // APIリクエストを実行し、レスポンスを待つ
        const response = await fetch(`/api/mentor/chats/${this.mentorComment.id}.json`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json; charset=utf-8',
            'X-Requested-With': 'XMLHttpRequest',
            'X-CSRF-Token': CSRF.getToken()
          },
          credentials: 'same-origin',
          redirect: 'manual',
          body: JSON.stringify(params)
        });

        // レスポンスが成功でない場合（4xx, 5xxエラーなど）
        if (!response.ok) {
          throw new Error(`API Error: ${response.status} ${response.statusText}`);
        }

        // 更新後の内容は親が保持しているので、差し替えを依頼する
        // WebSocketを再開する場合は、ブロードキャストが描画するのでこのemitは不要になる
        this.$emit('update-chat', await response.json());

        // 成功したら編集モードを終了
        this.editing = false;
        this.displayToast('チャットを更新しました。');

      } catch (error) { // ネットワークエラーやAPIエラーが発生した場合の処理
        console.error('更新に失敗しました:', error);
        this.displayToast('更新に失敗しました。時間をおいて再度お試しください。');
      } finally {
        this.isUpdating = false;
        this.$nextTick(() => {
          Prism.highlightAll(); // 元のコードにあった後処理
        })
      }
    },
    async deleteMentorComment() {
      if (window.confirm('削除してよろしいですか？')) {
        // 処理中フラグを立て、ボタンを無効化
        this.isDeleting = true;

        try {
          // APIリクエストを実行し、レスポンスを待つ
          const response = await fetch(`/api/mentor/chats/${this.mentorComment.id}.json`, {
            method: 'DELETE',
            headers: {
              'X-Requested-With': 'XMLHttpRequest',
              'X-CSRF-Token': CSRF.getToken()
            },
            credentials: 'same-origin',
            redirect: 'manual'
          });

          // レスポンスが成功でない場合（4xx, 5xxエラーなど）
          if (!response.ok) {
            // エラーオブジェクトを生成して、catchブロックに処理を移す
            throw new Error(`API Error: ${response.status} ${response.statusText}`);
          }

          // 一覧は親が保持しているので、除去を依頼する
          // WebSocketを再開する場合は、ブロードキャストが描画するのでこのemitは不要になる
          this.$emit('delete-chat', { mentor_comment_id: this.mentorComment.id })

          this.displayToast('チャットを削除しました。')
        } catch (error) { // ネットワークエラーやAPIエラーが発生した場合の処理
          console.error('削除に失敗しました:', error);
          this.displayToast('削除に失敗しました。時間をおいて再度お試しください。')
        } finally {
          this.isDeleting = false;
        }
      }
    },
    handleKeyDown(e) {
      if (document.activeElement != e.target) return;

      // Safariでの日本語変換確定時（keyCodeが229）のエンターキー操作を無視
      if (e.keyCode === 229) return;

      // 日本語変換中ならブロック
      if (this.isComposing) return

      // Shift + Enter押下は改行する
      if (e.shiftKey && e.key === 'Enter') {
        const textarea = e.target
        const start = textarea.selectionStart
        const end = textarea.selectionEnd
        this.body = this.body.substring(0, start) + '\n' + this.body.substring(end)
        this.$nextTick(() => {
          const position = start + 1
          textarea.selectionStart = position
          textarea.selectionEnd = position
        })
        e.preventDefault()
      }
      else if (e.key === 'Enter') {
        e.preventDefault()
        this.updateMentorComment()
      }
    },
    /**
     * バッジがクリックされた際に、フィルターを「設定」または「クリア」する処理。
     * このバッジが現在のフィルターに一致するかで挙動をトグルします。
     */
    toggleFilter() {
      if (this.isActiveFilterBadge) {
        // もしこのバッジが現在選択されているフィルターなら、フィルターをクリアする
        this.$emit('clear-filter');
      } else {
        // もしこのバッジが現在選択されていないフィルターなら、このバッジのフィルターを設定する
        this.$emit('set-filter', this.commentFilterValue); // コメント自身のフィルター値を親に渡す
      }
    },
    dropdownToggle: function () {
      // this.dropdown = !this.dropdown
      // リアクションの中のトグルクリック
    },
  }
}
</script>